import { type ConfigIngestModes, type Permission } from './recordingTypes';

export type OrganizationType = 'academic' | 'corporate';
export type HostType = 'instructor' | 'teacher' | 'professor' | 'host' | 'faculty' | 'producer' | 'facilitator';
export type AssistantType =
    | 'assistant'
    | 'faculty'
    | 'co_host'
    | 'adj_faculty'
    | 'adj_facilitator'
    | 'planner'
    | 'producer';
export type StudentType = 'student' | 'participant' | 'learner';
export type RosterType = 'roster' | 'roll' | 'participants';
export type GradebookType = 'gradebook' | 'reports' | 'results' | 'outcomes' | 'feedback' | 'completions' | 'credits';
export type ProctoringType = 'proctoring_view' | 'collaboration_view';
export type DocumentType =
    | 'syllabus'
    | 'rubric'
    | 'outline'
    | 'agenda'
    | 'schedule'
    | 'overview'
    | 'curriculum'
    | 'program';
export type ZoomAppId = 'user' | 'collab' | 'teams';

export interface TerminologySettings {
    hostTitle: HostType;
    assistantsTitle: AssistantType;
    studentsTitle: StudentType;
    rosterTitle: RosterType;
    gradebookTitle: GradebookType;
    documentTitle: DocumentType;
    proctoringTitle: ProctoringType;
}

/**
 * Intended behaviour when an user attempts to access a class, but
 * is not on the class roster.
 */
export enum UnauthUserAccess {
    /** Allow access, but flag them as "unverified" (legacy behaviour) */
    Allow = 'allow',
    /** Allow access and enroll user as a guest and creating a temporary user if not authenticated */
    AllowAsGuest = 'allow_guest',
    /** Allow access and enroll user as a student and creating a temporary user if not authenticated */
    AllowAsStudent = 'allow_student',
    /** Deny access */
    Deny = 'deny',
    /** Dont allow user to join, but for non authentiated users provide a link to sign in */
    SignIn = 'signin'
}

/**
 * Intended behaviour when an authenticated user attempts to access a class, but
 * is not on the class roster.
 */
export enum UnenrolledUserAccess {
    /** Allow access, but flag them as "unverified" (legacy behaviour) */
    Allow = 'allow',
    /** Allow access and enroll user as a guest */
    AllowAsGuest = 'allow_guest',
    /** Allow access and enroll user as a student */
    AllowAsStudent = 'allow_student',
    /** Deny access */
    Deny = 'deny'
}

export enum ClassAccess {
    All = 'all',
    App = 'app',
    Lti = 'lti',
    None = 'none'
}

export enum WebExpOptions {
    Classic = 'classic',
    NextGen = 'nextgen'
}

export enum ClassJoinPermission {
    RosterOnly = 'roster only',
    AuthenticatedUsers = 'authenticated users',
    All = 'all'
}

export enum ScreenShareResolutions {
    Low = 'Low',
    Medium = 'Medium',
    High = 'High'
}

export interface ZoomSettings {
    useCustomPasscodeRequirements: boolean;
    minPasscodeLength: boolean;
    passcodeLength: number;
    requireLetter: boolean;
    requireNumber: boolean;
    requireSpecialCharacter: boolean;
    requireUpperLowerCase: boolean;
    noConsecutiveCharacters: boolean;
    consecutiveCharacterLength: number;
    onlyNumericPasscode: boolean;
}

export enum ChatPermission {
    InstructorOnly = 'instructor-only',
    EveryoneDirectly = 'everyone-directly',
    EveryonePublicly = 'everyone-publicly',
    NoOne = 'noone',
    Blank = ''
}

// All options here should be flat. No nested objects
export type OverridableClassOptions = {
    unauthUserAccess: UnauthUserAccess;
    unauthUserText: string | null;
    unenrolledUserAccess: UnenrolledUserAccess;
    unenrolledUserText: string | null;
    allowVanillaZoom: boolean;
    classJoinPermission: ClassJoinPermission;
    rosterSelfEnrollment: boolean;
    hasAttendance: boolean;
    attendanceGradeTardyScore: number; // the percentage of credit given to students who are tardy, default is 80%
    showAttendanceGrade: boolean; // overrides the attendance grade visibility in the lms settings
    attendanceAutoLateThresholdMinutes: number; // the number of miuntes after the start of class that a student is considered late
    hasAiTa: boolean;
    hasFileAnnotations: boolean;
    hasPresentations: boolean;
    enablePrivateDiscussion: boolean;
    orgScreenshareRes?: ScreenShareResolutions;
    recordingInitialPermission?: Permission;
    recordingsRestrictChatParticipants: boolean;
    hasTelephony: boolean;
    autoJoinWhenSessionActive: boolean;
    allowInstructorAutoJoinPriorMinutes: number;
    allowStudentJoinPriorMinutes: number;

    //following settings are removed from UI, we can remove them
    webExperience: WebExpOptions;
    allowNextGenMobile: boolean;
};

export type AttendanceSettings = {
    hasAttendance: boolean;
    showAttendanceGrade: boolean;
    attendanceTabAccess: ClassAccess;
    attendanceGradeTardyScore: number;
    attendanceGradeTardyScorePercent: number;
    attendanceAutoLateThresholdMinutes: number;
    attendanceAutoLateThresholdMinutesOverride: ClassLevelOverrideOptions;
    attendanceGradeTardyScoreOverride: ClassLevelOverrideOptions;
    enablePerSessionAttendanceOptions: boolean;
};

export enum ClassLevelOverrideOptions {
    NoOne = 'no one',
    AdminsOnly = 'admins only',
    AdminsAndInstructors = 'admins and instructors'
}

export type ClassOptionsOverrides = {
    [P in keyof OverridableClassOptions]?: ClassLevelOverrideOptions;
};

export type ClassLevelOptions = Partial<OverridableClassOptions>;

export interface SchoolLevelOptions extends OverridableClassOptions {
    forceWebAsChromebook?: boolean;
    azureTenantIds: string[];
    zoomAccountNumbers: number[];
    userAccountCreation?: UserAccountCreationOptions; // Default depends on if Teams
    userAccountCreationDomains: string[];
    salesTrainingMode: boolean;
    allowTabLocking: boolean;
    autoProvisionNewUsersZoom: boolean;
    autoProvisionNewUsersMicrosoft: boolean;
    disableTeamsRecording: boolean;
    bulkRecordingOperations: boolean;
    hasTeamsLiveCaptioning: boolean;
    subClasses: boolean;
    updateMicrosoftLicenseDays: number;
    orgScreenshareRes: ScreenShareResolutions;
    hasMoodle: boolean;
    hasNextGenPolls: boolean;
    hasNextGenFiles: boolean;
    hasNextGenGoogleDrive: boolean;
    hasNextGenOneDrive: boolean;
    hasTabScreenshare: boolean;
    hasTabScreenshareShareAudio: boolean;
    hasTabScreenshareAnnotation: boolean;
    enableNextGenFilesGoogleServiceAccount: boolean;
    allowVirtualBackgroundInModeratePerfMode: boolean;
    hasAllBreakoutTranscripts: boolean;
    hasEndUserSurvey: InstructorTARestrictedAccess;
    hasEndUserPerformanceTest: boolean;
    allowCustomVirtualBackgroundUpload: boolean;
    disableCameraMicrophoneWhenRecording: boolean;
    hasNextGenSidebar: boolean;
    enableACS25Videos: boolean;
    recordingsRestrictChatParticipants: boolean;
    recordingsDisablePublicOption: boolean;
    recordingsDisableDownloads: boolean;
    recordingsExcludeStudentsActiveSpeaker: boolean;
    enableLearnerEngagement: boolean;
    participationSort: ParticipationSortType;
    hasInMeetingTechCheck: boolean;
    hasNextGenChat: boolean;
    hasWhiteboardBORType: boolean;
    permittedEmbedDomains: string[];
    excludedEmbedDomains: string[];
    hasSSOFeature: boolean;
    hasClassAuth: boolean;
    hasMsftAuth: boolean;
    hasScheduleLinkOnClassLanding: boolean;
    hasAuthOnClassLanding: boolean;
    useIdentifierInRoster: boolean;
    allowLmsGroupBreakouts: boolean;
    disableClassBackgrounds: boolean;
    hasNextGenChatActivityFeed: boolean;
    hasNextGenLearningToolSidebar: boolean;
    hasSyncSeatingCharts: boolean;
    allowInstructorsTranscriptDownload: boolean;
    hasBreakoutRoomCrossCommunication: boolean;
    showAdvancedFollowMeOptions: boolean;
    classInvitationEmailAddressOverride: string;
    zoomSettings: ZoomSettings;
    useSettingsForAttendanceGrade: boolean;
    enablePerSessionAttendanceOptions: boolean;
    enableLearningToolsTransition: boolean;
    defaultChatPermissions: ChatPermission;
    allowVerification: boolean;
    allowNextGenGuests: boolean;
    hasAttendance: boolean;
    hasLiveTranslation: boolean;
    hasConfigurableUI: boolean;
    offerBreakoutTranscripts: boolean;
    openIdPrompt: OpenIdPromptType[];
    allowWaitingRoom: boolean;
}

export enum UserAccountCreationOptions {
    AdminOnly = 'admin',
    Domains = 'domains',
    AllUsers = 'all'
}

export enum InstructorTARestrictedAccess {
    Everyone = 'everyone',
    InstructorsTAsOnly = 'instructor_ta',
    NoOne = 'no_one'
}

export enum ParticipationSortType {
    Engagement = 'engagement',
    TalkTime = 'talkTime'
}

export enum OpenIdPromptType {
    NotSpecified = 'not_specified',
    None = 'none',
    Login = 'login',
    Consent = 'consent',
    SelectAccount = 'select_account'
}
export interface FeatureSettings {
    classDocument: boolean;
    classGradebook: boolean;
    classAssessments: boolean;
    lms: boolean;
    surveys: boolean;
    polling: boolean;
    proctoring: boolean;
    enableLogAutoUpload: boolean;
    hasShareFiles: boolean;
    whiteboard: boolean;
    assignments: boolean;
    chat: boolean;
    loseFocusTracking: boolean;
    webApp: boolean;
    forceWebAppChromebooks: boolean;
    allowApp: boolean;
    autoTranscriptions: boolean;
    classNotes: boolean;
    sendInviteEmails: boolean;
    showMeetingInfo: boolean;
    promptTeachersForFeedback: boolean;
    promptStudentsForFeedback: boolean;
    notifyOutdatedClassVersion: boolean;
    enforceClassMinimumVersion: boolean;
    recordingLegacyZoomList: boolean;
    recordingInitialPermission: Permission;
    recordingIngestMode: ConfigIngestModes;
    recordingParticipantsCanView: boolean;
    recordingRestrictGallery: boolean;
    recordingTabAccess: ClassAccess;
    attendanceTabAccess: ClassAccess;
    restrictSessionsRecordingsByGroups: boolean;
    google: boolean;
    msOffice: boolean;
    aiTa: boolean;
    classAccess: ClassAccess;
    allowInstructorSwap: boolean;
    enableBreakoutRoomCloudRecording: boolean;
    preventLeaveSessionRedirect: boolean;
    whiteboardScreenshotFeature: boolean;
    instructorChatDownload: boolean;
    isBbData: boolean;
    dataPortalAdmin: boolean;
    dataPortalBusinessAnalyst: boolean;
    dataPortalClassBusinessAnalyst: boolean;
    dataPortalCustomerSupport: boolean;
    dataPortalInstructor: boolean;
    dataPortalStudent: boolean;
    allowAiForSupport: boolean;
    allowAiForInsights: boolean;
    options: SchoolLevelOptions;
    overrides: ClassOptionsOverrides;
    //following settings are removed from UI, they were not implemented in WebApp, we can remove them
    enableBreakoutRoomSecurity: boolean;
    hasPanicMode: boolean;
    allowZoom: boolean;
}

export interface AuthSettings {
    vanityUrl: string;
    domains: string[];
    joinMeetingButton: boolean;
    allowPMI: boolean;
    forcedAuthentication: boolean;
    autoVerify: 'never' | 'optional' | 'always';
    unauthUserAccess: UnauthUserAccess;
    unauthUserText: string | null;
    unenrolledUserAccess: UnenrolledUserAccess;
    unenrolledUserText: string | null;
}

export interface RecordingSettings {
    autoSave: boolean;
    cloudSave: boolean;
    localSave: boolean;
    participantsCanDownload: boolean;
}

export interface ClassForTeamsSettings {
    azureTenantIds: string[];
    updateMicrosoftLicenseDays: number;
    hasAzurePhoneNumbers: boolean;
}

export interface OrgSettings {
    prefix: string;
    organizationType: OrganizationType;
    terminologySettings: TerminologySettings;
    featureSettings: FeatureSettings;
    recordingSettings: RecordingSettings;
    authSettings: AuthSettings;
    classForTeamsSettings: ClassForTeamsSettings;
    zoomSettings: ZoomSettings;
    zoomAppId: ZoomAppId;
}

export interface DefaultSettings {
    academic: OrgSettings;
    corporate: OrgSettings;
}
